import { useMemo } from 'react';
import { Button } from '@aws-amplify/ui-react';
import { PersonDetailActivity } from './PersonDetailActivity';
import { PersonDetailHeader } from './PersonDetailHeader';
import { PersonDetailRoles } from './PersonDetailRoles';
import './PersonDetailKeystone.scss';

interface Evidence {
  firstName?: string;
  middleName?: string;
  lastName?: string;
}

interface Role {
  role?: string;
  area?: string;
  modifier?: string;
  title?: string;
  evidence?: Evidence[];
  institution?: {
    id: string;
    name?: string;
    type?: string;
    loc_state?: string;
  };
}

interface Bio {
  bio: string;
  created_at?: string;
}

interface KeystonePerson {
  id: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  emailVerificationStatus?: string;
  emailVerifiedAt?: string;
  phone?: string;
  roles?: Role[];
  bios?: Bio[];
}

// Extract unique observed names from all evidence
function getUniqueObservedNames(roles: Role[]): string[] {
  const namesSet = new Set<string>();

  for (const role of roles) {
    if (role.evidence) {
      for (const e of role.evidence) {
        const nameParts = [e.firstName, e.middleName, e.lastName].filter(Boolean);
        if (nameParts.length > 0) {
          namesSet.add(nameParts.join(' '));
        }
      }
    }
  }

  return Array.from(namesSet);
}

interface PersonDetailKeystoneProps {
  person: KeystonePerson;
  onExport: () => void;
  isExportLoading: boolean;
}

export function PersonDetailKeystone({ person, onExport, isExportLoading }: PersonDetailKeystoneProps) {
  // Compute unique observed names once
  const uniqueObservedNames = useMemo(
    () => getUniqueObservedNames(person.roles || []),
    [person.roles]
  );

  // Get display name to filter out from alternative names
  const displayName = person.name || `${person.firstName || ''} ${person.lastName || ''}`.trim();

  // Filter out the display name to get alternative names
  const alternativeNames = useMemo(() => {
    const displayNameLower = displayName.toLowerCase();
    return uniqueObservedNames.filter(name => name.toLowerCase() !== displayNameLower);
  }, [uniqueObservedNames, displayName]);

  const exportButton = (
    <Button
      onClick={onExport}
      size="small"
      className="tiny"
      isLoading={isExportLoading}
      isDisabled={isExportLoading}
    >
      Export
    </Button>
  );

  return (
    <div className="person-detail-keystone">
      <div className="person-detail-keystone__header-row">
        <div className="person-detail-keystone__header-container content content-800 plain">
          <PersonDetailHeader
            person={person}
            alternativeNames={alternativeNames}
            actionButton={exportButton}
          />
        </div>
      </div>
      <div className="content content-800 plain">
        {person.bios && person.bios.length > 0 && person.bios[0].bio && (
          <div className="person-detail-keystone__bio-section">
            <h3 className="person-detail-keystone__bio-title">Bio</h3>
            <div className="person-detail-keystone__bio">
              <div className="person-detail-keystone__bio-text">
                {person.bios[0].bio}
              </div>
            </div>
          </div>
        )}
        <PersonDetailRoles roles={person.roles || []} />
        <PersonDetailActivity personId={person.id} />
      </div>
    </div>
  );
}
